import { createSlice } from "@reduxjs/toolkit";
import axiosInstanceWebPortal from "../helper/axiosInstanceWebPortal";
import { toast } from "react-toastify";
import { setBusinessUserDetails, logoutBusinessLocal } from "./businessAuthSlice";
import { getWebDeviceInfo } from "../components/Utils/getWebDeviceInfo ";

const initialState = {
  userType: "UNREGISTERED", // "businessUser" | "customerUser"
  userDetails: null,
  accountType: null, // "business" | "customer"
  loginLoading: false,
};

export const userManagementAuthSlice = createSlice({
  name: "userManagementAuth",
  initialState,
  reducers: {
    umpLoginStart: (state) => {
      state.loginLoading = true;
    },
    umpLoginFailed: (state) => {
      state.loginLoading = false;
    },
    umpLoginSuccess: (state, action) => {
      state.loginLoading = false;
      state.userDetails = action.payload.userDetails;
      state.userType = action.payload.userType;
      state.accountType = action.payload.accountType;
    },
    umpLogoutLocal: (state) => {
      state.userType = "UNREGISTERED";
      state.userDetails = null;
      state.accountType = null;
      sessionStorage.removeItem("umpToken");
      sessionStorage.removeItem("umpRefreshToken");
      sessionStorage.removeItem("umpUserDetails");
    },
  },
});

export const { umpLoginStart, umpLoginFailed, umpLoginSuccess, umpLogoutLocal } =
  userManagementAuthSlice.actions;

export const umpLoginAsync = (UserData) => async (dispatch) => {
  dispatch(umpLoginStart());
  try {
    const deviceInfo = await getWebDeviceInfo();
    
    const data = {
      userId: UserData?.userId.trim(),
      password:UserData?.password,
      accountType:UserData?.accountType,
      deviceName: deviceInfo?.deviceName,
      deviceId: deviceInfo?.deviceId,
      osVersion: deviceInfo?.osVersion,
      platform: deviceInfo?.platform,
      appVersion: deviceInfo?.appVersion,
      deviceToken: deviceInfo?.deviceToken,
      loginSource: "web-view",
    };

    const response = await axiosInstanceWebPortal.post("/upload/userLogin", data);

    if (response?.status === 200 || response?.status === 201) {
      const respAccountType = response?.data?.response?.accountType;

      if (respAccountType === "admin" || respAccountType === "masteradmin") {
        dispatch(umpLoginFailed());
        toast.error("Admin accounts cannot access this portal.");
        return null;
      }

      const token = response.headers["token"];
      const refreshToken = response.headers["refreshtoken"];
      const userDetails = response.data?.response;

      // Store UMP tokens
      sessionStorage.setItem("umpToken", JSON.stringify(token));
      sessionStorage.setItem("umpRefreshToken", JSON.stringify(refreshToken));
      sessionStorage.setItem("umpUserDetails", JSON.stringify(userDetails));

      // Set as active axios token
      sessionStorage.setItem("userLoggedToken", JSON.stringify(token));
      sessionStorage.setItem("refreshToken", JSON.stringify(refreshToken));

      const userType = respAccountType === "business" ? "businessUser" : "customerUser";

      dispatch(umpLoginSuccess({ userDetails, userType, accountType: respAccountType }));

      // For business users: populate businessAuth.userDetails so subscription thunks work,
      // but do NOT set userType or loginSource — this keeps /businessSubscriptionPortal locked.
      if (respAccountType === "business") {
        sessionStorage.setItem("businessToken", JSON.stringify(token));
        sessionStorage.setItem("businessRefreshToken", JSON.stringify(refreshToken));
        sessionStorage.setItem("businessUserDetails", JSON.stringify(userDetails));
        dispatch(setBusinessUserDetails(userDetails)); // userType stays "UNREGISTERED"
      }

      toast.success("Login successful!");
      return response;
    }

    dispatch(umpLoginFailed());
    toast.error("Login failed. Please check your credentials.");
    return null;
  } catch (error) {
    dispatch(umpLoginFailed());
    toast.error(
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "Login failed."
    );
    return null;
  }
};

export const umpLogoutAsync = () => (dispatch) => {
  // Clear UMP session storage
  sessionStorage.removeItem("umpToken");
  sessionStorage.removeItem("umpRefreshToken");
  sessionStorage.removeItem("umpUserDetails");
  sessionStorage.removeItem("userLoggedToken");
  sessionStorage.removeItem("refreshToken");
  // Clear business session if it was set from UMP
  dispatch(umpLogoutLocal());
  dispatch(logoutBusinessLocal());
};

export default userManagementAuthSlice.reducer;
